import type { Database } from 'node-sqlite3-wasm';
import type { DbTagRow } from '../../resolution/types.js';
import { listEffectiveIndexedFilePaths, loadFileImports } from '../storage/symbol-parent-file-map.js';
import { getSymbolIndexParentContextById } from './symbol-index-sqlite.js';

export type DbImportRow = {
  file_path: string;
  local_name: string;
  source_name: string;
  module_specifier: string;
  source_file_path: string | null;
  import_kind: string;
  start_line: number;
  start_column: number;
};

export function listImportsForFile(db: Database, parentId: number, filePath: string): DbImportRow[] {
  const ctx = getSymbolIndexParentContextById(db, parentId);
  if (ctx == null) return [];
  return loadFileImports(db, ctx, filePath);
}

export function listAllImportsForParent(db: Database, parentId: number): DbImportRow[] {
  const ctx = getSymbolIndexParentContextById(db, parentId);
  if (ctx == null) return [];
  const imports: DbImportRow[] = [];
  for (const filePath of listEffectiveIndexedFilePaths(db, ctx)) {
    imports.push(...loadFileImports(db, ctx, filePath));
  }
  return imports;
}

function prefersTypeImport(refKind: string | undefined): boolean {
  return refKind === 'type';
}

function prefersValueImport(refKind: string | undefined): boolean {
  return refKind != null && refKind !== 'type';
}

/** Pick the import binding that matches a reference, preferring type/value imports when ambiguous. */
export function findImportBindingForLocalName(
  imports: readonly DbImportRow[],
  localName: string,
  ref?: Pick<DbTagRow, 'kind'>,
): DbImportRow | null {
  const matches = imports.filter((row) => row.local_name === localName);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;

  if (prefersTypeImport(ref?.kind)) {
    const typeImports = matches.filter((row) => row.import_kind === 'type');
    if (typeImports.length > 0) return typeImports[typeImports.length - 1]!;
  }

  if (prefersValueImport(ref?.kind)) {
    const valueImports = matches.filter((row) => row.import_kind !== 'type');
    if (valueImports.length > 0) return valueImports[valueImports.length - 1]!;
  }

  return matches[matches.length - 1]!;
}
